// Default categories.json seed — written to the data folder on first run
// when the folder doesn't already have one. Keep in sync with
// src-tauri/src/seeds/categories.json so a folder created by the web
// app is interchangeable with one created by the desktop app.
//
// Last synced from desktop main: 2026-05-14 — version 2 + 18 additional
// categories (Equipment & Capex, Depreciation, R&D, Memberships, Education,
// Bad Debt, FX Gain/Loss, Refunds & Chargebacks, Penalties & Fines,
// Charitable Contributions (Business), Marketplace Fees, Returns & Refunds
// (E-commerce), Fulfillment & Warehousing, Inventory Adjustments, Property
// Management, Property Taxes, HOA & Condo Fees, Property Maintenance).

export const DEFAULT_CATEGORIES = {
  _meta: {
    version: 2,
    notes:
      "Edit freely, or add new categories from the dashboard. 'kind' is one of 'income', 'expense', 'transfer'. Subcategories are open-ended: they can be sub-classes ('Coffee Shop') OR specific vendor names ('GitHub', 'Stripe'). Business categories below are sourced from QuickBooks Online + Wave defaults.",
  },
  categories: [
    // ── Personal / household ───────────────────────────────────────
    {
      name: "Income",
      kind: "income",
      subcategories: [
        "Salary", "Bonus", "Refund", "Interest", "Dividend", "Gift",
        "Reimbursement", "Sales Revenue", "Service Revenue",
        "Consulting Income", "Other Income",
      ],
    },
    {
      name: "Groceries",
      kind: "expense",
      subcategories: ["Supermarket", "Produce", "Meat", "Halal Butcher", "Bakery", "Specialty/Ethnic"],
    },
    {
      name: "Restaurants",
      kind: "expense",
      subcategories: ["Coffee Shop", "Fast Food", "Dine-in", "Takeout/Delivery", "Snacks/Drinks"],
    },
    {
      name: "Travel",
      kind: "expense",
      subcategories: ["Flights", "Hotel", "Car Rental", "Rideshare/Taxi", "Public Transit", "Gas/Fuel", "Parking/Tolls"],
    },
    {
      name: "Shopping",
      kind: "expense",
      subcategories: ["Clothing", "Electronics", "Home Goods", "Personal Care", "Books/Media", "Gifts"],
    },
    {
      name: "Household",
      kind: "expense",
      subcategories: ["Rent/Mortgage", "Utilities", "Internet/Phone", "Repairs/Maintenance", "Furniture", "Cleaning Supplies"],
    },
    {
      name: "Health",
      kind: "expense",
      subcategories: ["Pharmacy", "Doctor/Clinic", "Dental", "Vision", "Insurance", "Fitness"],
    },
    {
      name: "Kids",
      kind: "expense",
      subcategories: ["School", "Childcare", "Activities", "Toys", "Clothing"],
    },
    {
      name: "Entertainment",
      kind: "expense",
      subcategories: ["Streaming", "Events/Tickets", "Hobbies", "Games"],
    },
    {
      name: "Charity",
      kind: "expense",
      subcategories: ["Zakat", "Sadaqah", "Donations"],
    },
    {
      name: "Financial",
      kind: "expense",
      subcategories: ["Bank Fees", "Interest Charges", "Investment", "Taxes"],
    },
    {
      name: "Subscriptions",
      kind: "expense",
      subcategories: ["Software", "Cloud Storage", "News/Magazines", "Memberships"],
    },
    {
      name: "Auto",
      kind: "expense",
      subcategories: ["Insurance", "Maintenance", "Registration", "Loan/Lease"],
    },
    {
      name: "Transfer",
      kind: "transfer",
      subcategories: ["Internal", "Inter-Portal"],
    },

    // ── Business / corporate (QuickBooks Online + Wave defaults) ───
    {
      name: "Advertising & Marketing",
      kind: "expense",
      subcategories: [
        "Online Ads", "Social Media Ads", "Print/Outdoor", "SEO/SEM",
        "Sponsorships", "Email Marketing", "Branding/Design", "Promotional Items",
      ],
    },
    {
      name: "Software & Tools",
      kind: "expense",
      subcategories: [
        "Cloud Hosting", "SaaS Tools", "Development Tools", "Productivity",
        "Design", "Communications", "CRM", "Accounting Software",
        "Domain/DNS", "Security/Antivirus",
      ],
    },
    {
      name: "Payroll & Contractors",
      kind: "expense",
      subcategories: [
        "Wages", "Benefits", "Payroll Taxes", "Contractor Payments",
        "Payroll Service Fees", "Bonuses", "Commissions",
      ],
    },
    {
      name: "Office",
      kind: "expense",
      subcategories: [
        "Rent/Lease", "Utilities", "Internet/Phone", "Office Supplies",
        "Furniture", "Cleaning/Janitorial", "Coffee/Snacks", "Repairs/Maintenance",
      ],
    },
    {
      name: "Professional Services",
      kind: "expense",
      subcategories: ["Legal", "Accounting/Bookkeeping", "Consulting", "Tax Prep", "Other Professional"],
    },
    {
      name: "Business Insurance",
      kind: "expense",
      subcategories: [
        "General Liability", "Professional Liability", "Property",
        "Workers Comp", "Cyber", "Other Insurance",
      ],
    },
    {
      name: "Business Travel",
      kind: "expense",
      subcategories: [
        "Flights", "Hotel", "Rideshare/Taxi", "Car Rental",
        "Meals (Travel)", "Conference Fees", "Parking/Tolls",
      ],
    },
    {
      name: "Meals & Entertainment (Business)",
      kind: "expense",
      subcategories: ["Client Meals", "Team Meals", "Coffee/Drinks", "Events/Entertainment"],
    },
    {
      name: "Shipping & Postage",
      kind: "expense",
      subcategories: ["FedEx", "UPS", "USPS/Canada Post", "DHL", "Packaging Supplies", "Other Shipping"],
    },
    {
      name: "Cost of Goods Sold",
      kind: "expense",
      subcategories: [
        "Materials/Supplies", "Inventory Purchase", "Manufacturing",
        "Freight In", "Direct Labor", "Subcontractors",
      ],
    },
    {
      name: "Hiring & HR",
      kind: "expense",
      subcategories: ["Job Boards", "Background Checks", "HRIS/HR Tools", "Training/Education", "Recruiting Fees"],
    },
    {
      name: "Taxes & Licenses",
      kind: "expense",
      subcategories: ["Business License", "Sales Tax Remittance", "Income Tax", "Property Tax", "Other Taxes/Fees"],
    },
    {
      name: "Bank & Payment Processing",
      kind: "expense",
      subcategories: [
        "Bank Fees", "Wire Fees", "Stripe Fees", "PayPal Fees",
        "Square Fees", "Currency Conversion", "Interest Charges", "Other Processing",
      ],
    },
    {
      name: "Owner & Distributions",
      kind: "transfer",
      subcategories: ["Owner Draw", "Owner Contribution", "Distributions", "Shareholder Loan"],
    },

    // ── Capex + accounting (added 2026-05-14 sync) ────────────────
    {
      name: "Equipment & Capital Expenses",
      kind: "expense",
      subcategories: [
        "Computers/Laptops", "Phones/Tablets", "Office Equipment", "Vehicles",
        "Machinery/Production", "Furniture (Capital)", "Software (Capital)", "Tools",
      ],
    },
    {
      name: "Depreciation & Amortization",
      kind: "expense",
      subcategories: [
        "Equipment Depreciation", "Software Amortization",
        "Building Depreciation", "Vehicle Depreciation", "Other Amortization",
      ],
    },
    {
      name: "Research & Development",
      kind: "expense",
      subcategories: [
        "SR&ED Eligible", "Salaries (R&D)", "Contractor (R&D)",
        "Materials (R&D)", "Patents/IP", "Lab/Testing",
      ],
    },
    {
      name: "Memberships & Dues",
      kind: "expense",
      subcategories: [
        "Industry Association", "Trade Group", "Chamber of Commerce",
        "Networking Org", "Professional License",
      ],
    },
    {
      name: "Education & Training",
      kind: "expense",
      subcategories: [
        "Conferences", "Courses/Tuition", "Books/Learning Subscriptions",
        "Certifications", "Coaching/Mentorship",
      ],
    },
    {
      name: "Bad Debt",
      kind: "expense",
      subcategories: ["Uncollectible Invoice", "Customer Default", "Bad Debt Recovery"],
    },
    {
      name: "FX Gain/Loss",
      kind: "expense",
      subcategories: [
        "Realized FX Loss", "Realized FX Gain", "Unrealized FX",
        "Conversion Loss", "Conversion Gain",
      ],
    },
    {
      name: "Refunds & Chargebacks",
      kind: "expense",
      subcategories: [
        "Customer Refund", "Stripe Chargeback", "PayPal Dispute",
        "Order Cancellation", "Goodwill Refund",
      ],
    },
    {
      name: "Penalties & Fines",
      kind: "expense",
      subcategories: [
        "Tax Penalty", "Late Filing Fee", "Regulatory Fine",
        "Bank Penalty", "Traffic/Parking",
      ],
    },
    {
      name: "Charitable Contributions (Business)",
      kind: "expense",
      subcategories: [
        "Donations (Business)", "Sponsorships (Charitable)", "In-Kind", "Matching Donations",
      ],
    },

    // ── E-commerce + marketplaces (added 2026-05-14 sync) ─────────
    {
      name: "Marketplace Fees",
      kind: "expense",
      subcategories: [
        "Amazon Seller Fees", "Etsy Fees", "eBay Fees", "Shopify Fees",
        "Walmart Marketplace", "Facebook Marketplace", "Other Marketplace",
      ],
    },
    {
      name: "Returns & Refunds (E-commerce)",
      kind: "expense",
      subcategories: [
        "Customer Returns", "Restocking Costs", "Damaged/Lost Inventory",
        "Refund Reversal Fee",
      ],
    },
    {
      name: "Fulfillment & Warehousing",
      kind: "expense",
      subcategories: [
        "FBA Fees", "ShipBob", "ShipStation", "Pick & Pack",
        "Warehouse Rent", "Storage Fees", "3PL Services",
      ],
    },
    {
      name: "Inventory Adjustments",
      kind: "expense",
      subcategories: [
        "Shrinkage/Loss", "Write-Down", "Damaged Goods", "Count Variance",
        "Inventory Returns",
      ],
    },

    // ── Real estate / property (added 2026-05-14 sync) ────────────
    {
      name: "Property Management",
      kind: "expense",
      subcategories: [
        "Manager Fees", "Leasing Fees", "Tenant Screening", "Eviction Costs",
        "Property Inspections", "Move-in/Move-out",
      ],
    },
    {
      name: "Property Taxes",
      kind: "expense",
      subcategories: ["Real Estate Tax", "Municipal Tax", "Property Assessment", "Tax Appeals"],
    },
    {
      name: "HOA & Condo Fees",
      kind: "expense",
      subcategories: ["HOA Dues", "Condo Fees", "Special Assessment", "Pool/Amenity Fees"],
    },
    {
      name: "Property Maintenance",
      kind: "expense",
      subcategories: [
        "Plumbing", "Electrical", "HVAC", "Landscaping", "Cleaning (Property)",
        "Pest Control", "Snow Removal", "Pool Service", "Painting", "Roofing",
        "Appliance Repair",
      ],
    },

    // ── T&E + business-specific (added 2026-05-14 second sync) ────
    {
      name: "Per Diem",
      kind: "expense",
      subcategories: ["Lodging Per Diem", "Meals Per Diem", "Incidentals", "Foreign Per Diem"],
    },
    {
      name: "Mileage",
      kind: "expense",
      subcategories: [
        "Business Miles", "Commuter Miles", "Medical/Charity Miles",
        "Car Allowance", "EV Charging",
      ],
    },
    {
      name: "Tolls & Parking",
      kind: "expense",
      subcategories: [
        "Highway Tolls", "Airport Parking", "Garage/Street Parking",
        "Parking Permits", "Bridge Tolls",
      ],
    },
    {
      name: "Cell Phone & Wireless",
      kind: "expense",
      subcategories: [
        "Mobile Plan", "Data Add-ons", "Hotspot/Tethering",
        "Phone Hardware", "International Roaming",
      ],
    },
    {
      name: "Conferences & Events",
      kind: "expense",
      subcategories: [
        "Conference Registration", "Trade Show Booth", "Event Sponsorships",
        "Networking Events", "Continuing Education Events",
      ],
    },
    {
      name: "Gifts (Business)",
      kind: "expense",
      subcategories: [
        "Client Gifts", "Employee Gifts", "Vendor/Partner Gifts",
        "Holiday Gifts", "Promotional Swag",
      ],
    },

    // ── Fallback ───────────────────────────────────────────────────
    {
      name: "Uncategorized",
      kind: "expense",
      subcategories: ["Needs Review"],
    },
  ],
} as const;

export interface CategoriesDoc {
  _meta?: { version: number; notes?: string };
  categories: Array<{
    name: string;
    kind: "income" | "expense" | "transfer";
    subcategories: string[];
  }>;
}
